import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { AUDIT_RESULT } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { IsGatekeeperEnabled, GatekeeperFlag } from 'src/context/Gatekeeper'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  CONTROL_CHECK_RESULT_PILL_COLOR,
  ControlListItemConfig,
  ControlListItemConfigMap,
  ControlListItemConfigurations,
} from './ControlListItem.constants'

export const getComplianceFrameworksForControl = (
  complianceFrameworks: ComplianceFramework[],
  currentIdList: string[],
): ComplianceFramework[] =>
  complianceFrameworks.filter((framework: ComplianceFramework) =>
    currentIdList?.includes(framework.getId()),
  ) || []

export const getMismatch = (
  complianceFrameworkIdsList: string[],
  complianceFrameworksInUse: ComplianceFramework[],
  hasAudit: boolean,
): boolean => {
  if (!hasAudit || !complianceFrameworkIdsList.length) {
    return false
  }
  return !complianceFrameworksInUse.some((framework) =>
    complianceFrameworkIdsList.includes(framework.getModelId()),
  )
}

export const getControlsListItemConfig = (
  config?: ControlListItemConfig,
): ControlListItemConfigMap =>
  config
    ? ControlListItemConfigurations[config]
    : ControlListItemConfigurations[ControlListItemConfig.DEFAULT]

// useShowControlRiskColumn reports whether this list should carry its Risks column.
//
// The column and its filter surface risk names, which sit behind CAN_VIEW_RISKS - a
// capability held independently of the CAN_VIEW_CONTROLS that gets a user onto these
// lists. ListControls withholds the risk data from those users server-side, so without
// this the column would render an empty cell on every row.
export const useShowControlRiskColumn = (
  config?: ControlListItemConfig,
): boolean => {
  const canViewRisks = useCanPerformAction(CAPABILITY.CAN_VIEW_RISKS)
  return !!getControlsListItemConfig(config).showRiskColumn && canViewRisks
}

export const getControlsListItemTemplateColumns = (
  showContentMatch: boolean,
  canViewAuditScans: boolean,
  config?: ControlListItemConfig,
  showRiskColumn = false,
): string => {
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)
  // Only the configurations that render a Risks cell widen for it, and only when the
  // viewer may see risks, so the header, the rows and the track count stay in step.
  const withRisks = (t: string) => (showRiskColumn ? `${t} auto` : t)

  let template = ''
  switch (config) {
    case ControlListItemConfig.DEFAULT:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template

    case ControlListItemConfig.AI_DASHBOARD:
      if (canViewAuditScans && showLabels) {
        template = 'min-content fit-content(20ch) 1fr auto fit-content(200px)'
      } else if (canViewAuditScans && !showLabels) {
        template = 'min-content fit-content(20ch) 1fr fit-content(200px)'
      } else if (!canViewAuditScans && showLabels) {
        template = 'min-content fit-content(20ch) 1fr auto'
      } else {
        template = 'min-content fit-content(20ch) 1fr'
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.EVIDENCE:
      if (canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template

    case ControlListItemConfig.FRAMEWORK_REQUIREMENT:
      if (canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      } else {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(5, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.CONTROLS_BY_REQUIREMENT:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.EVIDENCE_VIA_REQUEST:
      if (canViewAuditScans && showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(8, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(7, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(7, auto)'
      } else {
        template = 'min-content min-content minmax(auto, 30%) repeat(6, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template
    default:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template
  }
}

export const getControlCheckResultPillLabel = (
  result: AUDIT_RESULT,
  testName?: CONTROL_TEST_NAMES,
): string => {
  switch (result) {
    case AUDIT_RESULT.PASS:
      return 'Passed'
    case AUDIT_RESULT.FAIL:
      return 'Failed'
    case AUDIT_RESULT.NOT_APPLICABLE:
      return 'Not Applicable'
    case AUDIT_RESULT.LAMBDA_ERROR:
    case AUDIT_RESULT.UNKNOWN:
      return 'Could not be evaluated'
    case AUDIT_RESULT.MISSING_TEST_PROCEDURES:
      return 'Missing test procedures'
    case AUDIT_RESULT.MISSING_SUGGESTIONS:
      return 'Missing required evidence'
    case AUDIT_RESULT.NOT_RUN:
    default:
      if (testName === CONTROL_TEST_NAMES.POLICY_MATCH) {
        return 'Not Examined'
      }
      return 'Not Tested'
  }
}

export const getControlCheckResultPillColor = (
  result: AUDIT_RESULT,
): string => {
  return CONTROL_CHECK_RESULT_PILL_COLOR[result]
}
